import { IEditorMetadata } from "@mctypes/global.types";
import { Box } from "@mantine/core";
import React from "react";
import { FailedAlert } from "../../../../components/";
import WidgetWrapper from "../../../../components/WidgetWrapper/WidgetWrapper";
import { useAppSelector } from "../../../../hooks";
import "./newsapi.enhancer.ts";
import MultiCommentMantine from "../../issuesApp/IssuesTab/IssueItemModal/new/MultiCommentMantine";
import {
  useGetProjectByProjectIdProjectdashboardAndDashboardIdNewsQuery,
  usePostProjectByProjectIdProjectdashboardAndDashboardIdNewsCommentReactionMutation,
  usePostProjectByProjectIdProjectdashboardNewsMutation,
  usePutProjectByProjectIdProjectdashboardNewsAndNewsItemIdMutation,
} from "../../../../../openapi";
import { selectNewsComments } from "./newsSelectors";

const News: React.FC = () => {
  const projId = useAppSelector((state) => state.adminReducer.project._id);
  const user = useAppSelector((state) => state.globalReducer.user);
  const layoutId = useAppSelector((state) => state.projectDashboardReducer.activeLayout._id);
  const members = useAppSelector((state) => state.adminReducer.project.members);
  const data = useAppSelector(selectNewsComments);
  const {
    data: rawData,
    isLoading,
    isError,
  } = useGetProjectByProjectIdProjectdashboardAndDashboardIdNewsQuery(
    {
      projectId: projId,
      dashboardId: layoutId,
    },
    {
      skip: layoutId == null,
    },
  );

  const [createNews, { isLoading: isLoadingCreate, isError: isErrorCreate }] =
    usePostProjectByProjectIdProjectdashboardNewsMutation();
  const [updateNews, { isLoading: isLoadingEdit, isError: isErrorUpdate }] =
    usePutProjectByProjectIdProjectdashboardNewsAndNewsItemIdMutation();
  const [reactToNews] = usePostProjectByProjectIdProjectdashboardAndDashboardIdNewsCommentReactionMutation();

  const failed = isError || isErrorCreate || isErrorUpdate;
  const loading = isLoading || isLoadingEdit || isLoadingCreate;

  return (
    <WidgetWrapper title="Oppslagstavle" widgetKey="news" allowFullscreen width={"800px"}>
      {failed && <FailedAlert errorMessage="Failed to start news" />}
      {data != null && user != null && failed !== true && layoutId != null && (
        <>
          <Box p="xs" flex={1} style={{ overflow: "auto" }}>
            <MultiCommentMantine
              members={members}
              sortDesc={true}
              alwaysShowInput={false}
              useBrowserPrompt={true}
              comments={data}
              showSearch={true}
              loading={loading}
              visibleCommentsOnLoad={5}
              offsetLeft={true}
              onCreate={(text: string, metadata: IEditorMetadata) => {
                createNews({
                  projectId: projId,
                  body: {
                    newsItem: {
                      dashboardId: layoutId,
                      text,
                      deleted: false,
                      metadata,
                    },
                  },
                });
              }}
              onDelete={(_id) => {
                updateNews({
                  projectId: projId,
                  newsItemId: _id,
                  body: {
                    newsItem: {
                      dashboard: layoutId,
                      text: rawData?.find((n) => n._id === _id)?.text ?? "",
                      deleted: true,
                    },
                  },
                });
              }}
              onSave={(_id, text, metadata) => {
                updateNews({
                  projectId: projId,
                  newsItemId: _id,
                  body: {
                    newsItem: {
                      dashboard: layoutId,
                      text,
                      deleted: false,
                      metadata,
                    },
                  },
                });
              }}
              loggedInUser={user}
              noCommentsText="Ingen nyheter å vise. Klikk på knappen i venstre hjørne for å lage et innlegg."
              onReact={(commentId, emoji) => {
                reactToNews({
                  projectId: projId,
                  dashboardId: layoutId,
                  body: { commentId, emoji },
                });
              }}
              onReactReply={(commentId, replyId, emoji) => {
                reactToNews({
                  projectId: projId,
                  dashboardId: layoutId,
                  body: { commentId, replyId, emoji },
                });
              }}
              onReply={(commentId, text, metadata) => {
                updateNews({
                  projectId: projId,
                  newsItemId: commentId,
                  body: {
                    newsItem: {
                      dashboard: layoutId,
                      reply: {
                        text,
                        metadata,
                      },
                    },
                  },
                });
              }}
              onSaveReply={(commentId, replyId, text, metadata) => {
                updateNews({
                  projectId: projId,
                  newsItemId: commentId,
                  body: {
                    newsItem: {
                      dashboard: layoutId,
                      reply: {
                        _id: replyId,
                        text,
                        metadata,
                      },
                    },
                  },
                });
              }}
              onDeleteReply={(commentId, replyId) => {
                updateNews({
                  projectId: projId,
                  newsItemId: commentId,
                  body: {
                    newsItem: {
                      dashboard: layoutId,
                      reply: {
                        _id: replyId,
                        deleted: true,
                      },
                    },
                  },
                });
              }}
              currentUserId={user._id}
            />
          </Box>
        </>
      )}
    </WidgetWrapper>
  );
};

export default News;
